import { Directive, Input } from '@angular/core';
import { AbstractControl, AsyncValidator, NG_ASYNC_VALIDATORS, ValidationErrors } from '@angular/forms';
import { IUserContactUniqueData } from '@fi-shared/fi-auth-shared/fi-authenticate-shared.service';
import { ContactService } from '@fi-shared/fi-contacts/contact.service';
import { FiContactsService } from '@fi-shared/fi-contacts/fi-contacts.service';

const time: number = 500;

export interface IFiUniqueContactData {
    userId: number;
    doCheck: boolean;
    isSignIn?: boolean;
    throwOnlyWhenContactUsedAsLogin?: boolean;
    prefixValue?: string;
    isDuplicate?: boolean;
}

@Directive({
    selector: '[fiUniqueContact]',
    providers: [
        {
            provide: NG_ASYNC_VALIDATORS,
            useExisting: FiUniqueContactAsyncValidatorDirective,
            multi: true,
        },
    ],
    standalone: false,
})
export class FiUniqueContactAsyncValidatorDirective implements AsyncValidator {
    @Input() fiUniqueContact: IFiUniqueContactData;

    constructor(
        private contactService: ContactService,
        private fiContactsService: FiContactsService,
    ) {}

    validateTimer: NodeJS.Timeout;

    validate(control: AbstractControl): Promise<ValidationErrors | null> {
        if (this.validateTimer) {
            clearTimeout(this.validateTimer);
            this.validateTimer = undefined;
        }

        return new Promise<ValidationErrors>((resolve, reject) => {
            this.validateTimer = setTimeout(() => {
                const controlValue: string = this.fiContactsService.getContactValueForValidation(
                    control.value,
                    this.fiUniqueContact.prefixValue,
                );
                if (!this.fiUniqueContact?.doCheck) resolve(null);

                this.isUniqueContact(controlValue, resolve, reject);
                return;
            }, time);
        });
    }

    private isUniqueContact(
        controlValue: string,
        resolve: (value: ValidationErrors) => void,
        reject: (value: ValidationErrors) => void,
    ) {
        this.contactService
            .isContactUnique(this.fiUniqueContact.userId, controlValue)
            .then((res: IUserContactUniqueData) => {
                if (res.IsUnique && !this.fiUniqueContact.isDuplicate) return resolve(null);

                if (this.fiUniqueContact.isSignIn) {
                    this.signInContactUniqueCheck(res, resolve);
                } else {
                    this.commonContactUniqueCheck(res, resolve);
                }

                return resolve(null);
            }, reject);
    }

    private signInContactUniqueCheck(res: IUserContactUniqueData, resolve: (value: ValidationErrors) => void) {
        if (!this.fiUniqueContact.throwOnlyWhenContactUsedAsLogin && !res.IsUnique)
            return resolve({ contact_not_unique: true });

        if (res.OtherUserHasContactAsLogin) return resolve({ contact_used_as_other_user_login: true });

        if (this.fiUniqueContact.isDuplicate) return resolve({ contact_duplicate: true });
    }

    private commonContactUniqueCheck(res: IUserContactUniqueData, resolve: (value: ValidationErrors) => void) {
        if (this.fiUniqueContact.isDuplicate) return resolve({ contact_duplicate: true });

        if (res.OtherUserHasContactAsLogin) return resolve({ contact_used_as_other_user_login: true });

        if (!this.fiUniqueContact.throwOnlyWhenContactUsedAsLogin) return resolve({ contact_not_unique: true });
    }
}
